import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CarmAngles } from '@/visualization/domain/carm-angles';
import { CARM_LIMITS } from '@/visualization/domain/carm-angles';
import type { CarmEditSnapshot } from '@/workflows/vascular/common/hooks/use-carm-edit';
import { formatCarmAngle } from '@/workflows/vascular/common/utils';

/** Chevron step in degrees per click (and per repeat while held). */
const CHEVRON_STEP = 2;
/** Hold-to-repeat: delay before repeating, then repeat period (ms). */
const HOLD_DELAY_MS = 400;
const HOLD_INTERVAL_MS = 150;

export interface CarmEditPanelProps {
    edit: CarmEditSnapshot;
}

/**
 * C-arm edit panel — overlay at the top-center of the 3D cell, rendered
 * only while a C-arm edit is active. Two body illustrations with a rotating
 * detector indicator (side view for CRA/CAU, feet view for LAO/RAO), a
 * column of angle steppers (CHEVRON_STEP degrees per click, hold to
 * repeat), and the commit/abandon buttons — the commit
 * button reads Confirm when creating a new preset, Update when adjusting an
 * existing one, and stays disabled until the draft has something worth
 * committing (an update whose angles have not moved yet). On narrow cells
 * (container below @sm) the body illustrations are hidden and only the angle
 * steppers remain, so the panel never overflows.
 */
export function CarmEditPanel({ edit }: CarmEditPanelProps) {
    const isNew = edit.target === 'new';
    return (
        <div className="pointer-events-none absolute inset-x-2 top-2 flex justify-center @container">
            <div
                className={cn(
                    'pointer-events-auto flex min-w-0 max-w-full flex-col items-center gap-1.5 p-2',
                    'rounded-md border border-border/50 bg-background/80 shadow-sm backdrop-blur-sm'
                )}
            >
                <div className="flex w-full items-center gap-3">
                    <BodyDial axis="craniocaudal" value={edit.angles.craniocaudal} />
                    <BodyDial axis="oblique" value={edit.angles.oblique} />
                    <div className="hidden h-16 w-px shrink-0 bg-border/50 @sm:block" />
                    <div className="flex shrink-0 flex-col gap-1">
                        <AngleStepper axis="craniocaudal" edit={edit} />
                        <AngleStepper axis="oblique" edit={edit} />
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    <Button
                        size="sm"
                        className="h-6 px-3 text-xs"
                        disabled={!edit.canCommit}
                        onClick={edit.confirm}
                    >
                        {isNew ? 'Confirm' : 'Update'}
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 px-3 text-xs" onClick={edit.cancel}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Angle stepper: chevrons + formatted value
// ============================================================================

function AngleStepper({ axis, edit }: { axis: keyof CarmAngles; edit: CarmEditSnapshot }) {
    const value = edit.angles[axis];
    const limit = CARM_LIMITS[axis];

    const decrement = useHoldRepeat(() => edit.adjust(axis, -CHEVRON_STEP));
    const increment = useHoldRepeat(() => edit.adjust(axis, CHEVRON_STEP));

    return (
        <div className="flex items-center gap-1">
            <ChevronButton
                direction="left"
                label={`Decrease ${axis}`}
                disabled={value <= -limit}
                onPress={decrement.start}
                onRelease={decrement.stop}
            />
            <span className="w-20 text-center font-mono text-xs tabular-nums text-foreground">
                {formatCarmAngle(value, axis)}
            </span>
            <ChevronButton
                direction="right"
                label={`Increase ${axis}`}
                disabled={value >= limit}
                onPress={increment.start}
                onRelease={increment.stop}
            />
        </div>
    );
}

function ChevronButton({
    direction,
    label,
    disabled,
    onPress,
    onRelease,
}: {
    direction: 'left' | 'right';
    label: string;
    disabled: boolean;
    onPress: () => void;
    onRelease: () => void;
}) {
    const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
    return (
        <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            disabled={disabled}
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            onPointerDown={onPress}
            onPointerUp={onRelease}
            onPointerLeave={onRelease}
        >
            <Icon className="h-3.5 w-3.5" />
        </Button>
    );
}

/** Hold-to-repeat: one step immediately, then repeat while held. */
function useHoldRepeat(onStep: () => void) {
    const stepRef = useRef(onStep);
    stepRef.current = onStep;
    const timeoutRef = useRef<number | null>(null);
    const intervalRef = useRef<number | null>(null);

    const stop = useMemo(() => {
        const handler = () => {
            window.removeEventListener('pointerup', handler);
            window.removeEventListener('pointercancel', handler);
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (intervalRef.current !== null) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
        return handler;
    }, []);

    const start = useCallback(() => {
        stop();
        stepRef.current();
        // A step can pin the angle at its limit, disabling the button while
        // the pointer is still down; disabled:pointer-events-none then
        // suppresses the button's own pointerup/leave — track the release on
        // window so the repeat always stops.
        window.addEventListener('pointerup', stop);
        window.addEventListener('pointercancel', stop);
        timeoutRef.current = window.setTimeout(() => {
            intervalRef.current = window.setInterval(() => stepRef.current(), HOLD_INTERVAL_MS);
        }, HOLD_DELAY_MS);
    }, [stop]);

    useEffect(() => stop, [stop]);

    return { start, stop };
}

// ============================================================================
// Body dial: patient illustration + detector indicator on the envelope arc
// ============================================================================

const DIAL_W = 128;
const DIAL_H = 96;
const DIAL_CX = 64;
const DIAL_CY = 62;
const RING_RADIUS = 44;
/** Width (dial units) the visible body is scaled to. */
const BODY_TARGET_WIDTH = 76;

/** Source image, cropped to the visible body, with its pixel dimensions. */
interface BodyImageConfig {
    href: string;
    w: number;
    h: number;
}

const BODY_IMAGES: Record<keyof CarmAngles, BodyImageConfig> = {
    // Patient supine seen from the side, head to the right
    craniocaudal: { href: '/human-body_side.png', w: 228, h: 51 },
    // Patient supine seen from the feet, anterior up
    oblique: { href: '/human-body_feet.png', w: 228, h: 65 },
};

/** Point on the dial: 0° at top, positive clockwise (SVG y grows downward). */
function polar(radius: number, deg: number): { x: number; y: number } {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: DIAL_CX + radius * Math.cos(rad), y: DIAL_CY + radius * Math.sin(rad) };
}

/**
 * Body illustration with an angle indicator: the drawing is static and the
 * marker rotates around it to point at the current angle. The arc spans
 * exactly the reachable envelope (±CARM_LIMITS), with ticks every 15°.
 *
 * The marker is the detector (image intensifier), the side the clinical angle
 * names refer to — the X-ray tube sits opposite, under the table at 0°.
 * Conventions (patient supine, detector anterior at 0°):
 * - craniocaudal: side view, head to the right — CRA tilts the detector
 *   toward the head (clockwise)
 * - oblique: view from the feet, patient left on the image right — LAO
 *   rotates the detector toward the patient's left (clockwise)
 */
function BodyDial({ axis, value }: { axis: keyof CarmAngles; value: number }) {
    const limit = CARM_LIMITS[axis];
    const image = BODY_IMAGES[axis];

    // Scale the image so the body spans BODY_TARGET_WIDTH, centered on the
    // dial center (= beam isocenter)
    const scale = BODY_TARGET_WIDTH / image.w;
    const imgX = DIAL_CX - (image.w / 2) * scale;
    const imgY = DIAL_CY - (image.h / 2) * scale;

    const arcStart = polar(RING_RADIUS, -limit);
    const arcEnd = polar(RING_RADIUS, limit);
    // Ticks at multiples of 15° within the envelope, symmetric around 0°
    // (the limit itself may not be a multiple — the arc ends mark it)
    const ticks: number[] = [];
    for (let deg = -Math.floor(limit / 15) * 15; deg <= limit; deg += 15) ticks.push(deg);

    const indicatorInner = polar(RING_RADIUS - 10, value);
    const indicatorOuter = polar(RING_RADIUS, value);

    return (
        <div className="hidden aspect-4/3 min-w-0 max-w-32 flex-1 @sm:block">
            <svg viewBox={`0 0 ${DIAL_W} ${DIAL_H}`} className="h-full w-full" aria-hidden="true">
                {/* Patient illustration */}
                <image href={image.href} x={imgX} y={imgY} width={image.w * scale} height={image.h * scale} />

                {/* Envelope arc + ticks */}
                <path
                    d={`M ${arcStart.x} ${arcStart.y} A ${RING_RADIUS} ${RING_RADIUS} 0 ${limit > 90 ? 1 : 0} 1 ${arcEnd.x} ${arcEnd.y}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-muted-foreground"
                />
                {ticks.map((deg) => {
                    const p1 = polar(RING_RADIUS - 2.5, deg);
                    const p2 = polar(RING_RADIUS + 2.5, deg);
                    return (
                        <line
                            key={deg}
                            x1={p1.x}
                            y1={p1.y}
                            x2={p2.x}
                            y2={p2.y}
                            stroke="currentColor"
                            strokeWidth="1"
                            className="text-muted-foreground"
                        />
                    );
                })}

                {/* Detector indicator */}
                <g className="text-primary">
                    <line
                        x1={indicatorInner.x}
                        y1={indicatorInner.y}
                        x2={indicatorOuter.x}
                        y2={indicatorOuter.y}
                        stroke="currentColor"
                        strokeWidth="2"
                    />
                    <circle cx={indicatorOuter.x} cy={indicatorOuter.y} r={3.5} fill="currentColor" />
                </g>
            </svg>
        </div>
    );
}
